import apiClient from 'services/api/apiService';
import { SortDirection } from 'components/Table/types';
import { IDeliveryDays } from 'components/DatePicker/DatePicker';
import {
  ICreateOrdersFromQuotationDTO,
  ICreateOrdersFromQuotationResult,
  ICreateQuotationDTO,
  IQuotationLinesResponse,
  IUpdateQuotationLineDTO,
} from 'types/Quotation.types';
import { getUrlWithQueryParams } from './api/getUrlWithQueryParams';

const BASE_URL = '/v1/quotations';

export const getQuotations = async (
  page?: number,
  perPage?: number,
  searchTerm?: string,
  sortBy?: string,
  sortDirection?: SortDirection
): Promise<any> => {
  const queryParamsObj = {
    page: page,
    per_page: perPage,
    search_term: searchTerm,
    sort_by: sortBy,
    sort_direction: sortDirection,
  };

  const url = `${BASE_URL}`;
  const fullUrl = getUrlWithQueryParams(url, queryParamsObj);
  const { data } = await apiClient.get<any>(fullUrl);
  return data.data;
};

export const createQuotation = async (
  dto: ICreateQuotationDTO
): Promise<any> => {
  const { data } = await apiClient.post<any>(BASE_URL, dto);
  return data.data;
};

export const getSingleQuotation = async (quotationId: string): Promise<any> => {
  const { data } = await apiClient.get<any>(`${BASE_URL}/${quotationId}`);
  return data.data;
};

// Delivery days for the lines of one supplier that are about to be ordered.
// Unlike the cart equivalent there is no cart to key off, so the selected
// quotation lines identify the shipment.
export const getQuotationDeliveryDays = async (
  quotationLineIds: number[],
  includeBuyerCalendar: boolean
): Promise<IDeliveryDays> => {
  const url = `${BASE_URL}/delivery-days`;
  const { data } = await apiClient.get<any>(url, {
    params: {
      quotation_line_ids: quotationLineIds.join(','),
      include_buyer_calendar: includeBuyerCalendar,
    },
  });
  return data.data;
};

// A partially successful run comes back as HTTP 400 carrying both the orders
// that were created and the ones that failed, so the same envelope has to be
// read from the rejected response too (see useCreateOrdersFromQuotation).
export const parseCreateOrdersFromQuotationResult = (
  responseBody: any
): ICreateOrdersFromQuotationResult => ({
  orders: responseBody?.data?.orders ?? [],
  errors: responseBody?.errors ?? [],
});

export const createOrdersFromQuotation = async (
  dto: ICreateOrdersFromQuotationDTO
): Promise<ICreateOrdersFromQuotationResult> => {
  const url = `${BASE_URL}/order`;
  const { data } = await apiClient.post<any>(url, dto);
  return parseCreateOrdersFromQuotationResult(data);
};

export const getQuotationLines = async (
  quotationId: string
): Promise<IQuotationLinesResponse> => {
  const url = `${BASE_URL}/${quotationId}/lines`;
  const { data } = await apiClient.get<any>(url);
  return data.data;
};

// Replaces the configuration of a single quotation line. Unlike the cart line
// equivalent this takes no quantity/pricing: only the answers change here.
export const updateQuotationLine = async (
  quotationId: string,
  quotationLineId: string,
  dto: IUpdateQuotationLineDTO
): Promise<any> => {
  const url = `${BASE_URL}/${quotationId}/lines/${quotationLineId}`;
  const { data } = await apiClient.patch<any>(url, dto);
  return data.data;
};

export const closeQuotation = async (quotationId: string): Promise<any> => {
  const url = `${BASE_URL}/${quotationId}/close`;
  const { data } = await apiClient.patch<any>(url);
  return data.data;
};

export const downloadQuotationPdf = async (
  quotationId: string
): Promise<Blob> => {
  const url = `${BASE_URL}/${quotationId}/export`;
  const { data } = await apiClient.post<Blob>(url, undefined, {
    responseType: 'blob',
  });
  return data;
};
